"use client";

/**
 * ScaleIndexDepth — what is in the index, how deeply it is filed, and what that costs to keep.
 *
 * WHY THIS REPLACED THE FLEET STRIP ON THIS PAGE. The strip that used to sit here showed pooled
 * memory, machines online, processor cores and shared card throughput. Those are the right figures
 * on /indexer_node, where the reader is deciding whether to lend a machine — they are the *offer*.
 * Here the reader is deciding whether the asset is worth backing, and cores are not the asset. A
 * page of hardware telemetry invites the response "so you own some computers", which is both true
 * and the least interesting thing on this page. What is actually unusual is the corpus: how much
 * of it there is, how finely it is cut, how much is known about each page, and how little it costs
 * to hold. So every tile here describes the INDEX, and the machinery is one link away.
 *
 * WHY THE PER-PAGE FIGURE IS THE ARGUMENT AND NOT A FOOTNOTE. Storage is the thing that decides
 * whether an index can keep growing without a balance sheet behind it. A page kept as raw markup,
 * with its scripts and styling and navigation furniture, is orders of magnitude larger than the
 * part anyone would ever search. Keeping only the cleaned text and the vectors is what makes the
 * next million pages a hardware question rather than a funding one — which is precisely the claim
 * this page is asking a reader to believe, expressed as a number the reader can watch move.
 *
 * NO ABSOLUTE SITE-WIDE TOTAL IS PUBLISHED HERE, and that is deliberate. `total_points` across
 * every collection is a disclosure about material held privately; `web.pages` and the ratios
 * derived from the same block are not. The collection COUNT is safe for the same reason — the list
 * is already public on /collections, and it is read from that same module so the two cannot drift.
 *
 * EVERY NUMBER COMES FROM THE FEED THE PUBLIC DASHBOARD READS — /live/storage, proxied as
 * /api/index/storage. Not a second source that agrees today and diverges next month: literally the
 * same endpoint behind askfinz.ai/indexing_in_progress, so a figure here and a figure there cannot
 * disagree. Nothing in this file is typed in by hand except the words.
 */

import { useEffect, useState } from "react";
import { Boxes, FileText, Layers, Scissors } from "lucide-react";
import { fmtCount } from "@/lib/format-stat";
import { StatTileGrid } from "@/components/marketing/stat-tile";
import { CORPORA } from "@/lib/corpora";

type Web = {
  pages?: number;
  chunks_per_page?: number;
  bytes_per_page_disk?: number;
  bytes_per_page?: number;
};

/** 21260 → "21 KB". Kept local and deliberately coarse: this is a tile, not a balance sheet. */
function perPage(n?: number): string {
  if (n == null || !isFinite(n) || n <= 0) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  if (n >= 1000) return `${Math.round(n / 1000)} KB`;
  return `${Math.round(n)} B`;
}

/* What the index knows about a page beyond its words. These are the payload fields actually
   written when a page is filed — not a wish list — which is why they are phrased as things the
   index holds rather than as capabilities. Cross-linking and categorisation are in here because
   they are the two that turn a pile of documents into something that can be reasoned over. */
const CARRIED = [
  "A quality score",
  "Heading structure",
  "Internal links out",
  "Language",
  "Published date",
  "Author and site",
  "Page type",
  "Structured data",
  "A subject collection",
  "A safety classification",
];

export function ScaleIndexDepth({ initial }: { initial?: Web | null } = {}) {
  /* Seeded by the server (ScaleIndexDepthSSR) so the figures are in the HTML on first paint rather
     than four dashes that fill in a beat after hydration. On a page whose whole argument is "these
     numbers are real, go and check them", opening on placeholders is the worst possible first
     impression. The client fetch below stays: the seed is only as fresh as the request. */
  const [web, setWeb] = useState<Web | null>(initial ?? null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const r = await fetch("/api/index/storage", { cache: "no-store" });
        if (!r.ok) return;
        const j = (await r.json()) as { web?: Web };
        if (on && j?.web) {
          setWeb(j.web);
          setLive(true);
        }
      } catch {
        /* transient — keep the last values on screen rather than blanking the section */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  const stats = [
    {
      icon: FileText,
      value: fmtCount(web?.pages ?? undefined),
      label: "Pages in the index",
      sub: "web pages, read and filed",
    },
    {
      icon: Boxes,
      /* Static, from the same module /collections renders — see the header note. */
      value: String(CORPORA.length),
      label: "Subject collections",
      sub: "filed beside the web pages",
    },
    {
      icon: Scissors,
      value: web?.chunks_per_page != null ? web.chunks_per_page.toFixed(1) : "—",
      label: "Passages per page",
      sub: "cut for retrieval, not stored whole",
    },
    {
      icon: Layers,
      /* On-disk rather than logical: what the volume actually holds is the figure that decides
         whether the next million pages need new hardware. */
      value: perPage(web?.bytes_per_page_disk ?? web?.bytes_per_page),
      label: "Kept per page",
      sub: "cleaned text and vectors",
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="kicker">
          {live ? "Live · the same feed the public dashboard reads" : "Connecting…"}
        </span>
      </div>

      <StatTileGrid items={stats} />

      <div className="grid gap-8 border-t hairline pt-8 md:grid-cols-2 md:gap-14">
        <div className="flex flex-col gap-3">
          <span className="kicker">Why the small number matters</span>
          <p className="text-[14.5px] leading-relaxed text-foreground/65">
            A web page kept whole — its markup, scripts, styling and navigation furniture — is
            vastly larger than the part anyone would ever search. askFinz keeps the cleaned text and
            the vectors and throws the rest away, which is why the figure above is measured in
            kilobytes rather than megabytes.
          </p>
          <p className="text-[14.5px] leading-relaxed text-foreground/65">
            That is the whole reason the index can keep growing on hardware rather than on a balance
            sheet. The next million pages are a question of adding machines to a pool, not of
            signing a larger contract with whoever owns the data.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <span className="kicker">What every stored page carries</span>
          <p className="text-[14.5px] leading-relaxed text-foreground/65">
            Filing a page is not the same as keeping it. Each one is scored, cut into passages,
            placed in a subject collection and described — so the index can be filtered and ranked
            on what a page <em>is</em>, not only on what it happens to say.
          </p>
          <ul className="mt-0.5 flex flex-wrap gap-1.5">
            {CARRIED.map((c) => (
              <li
                key={c}
                className="rounded-full border hairline px-2.5 py-1 text-[12px] text-foreground/65"
              >
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
